// Where a person came from, for the growth funnel (TF-126, docs/adr/0115-growth-funnel.md). The marketing
// site passes its visitor's first visit on in the links into the app (utm_* and tf_* parameters,
// src/lib/visit.ts in imiezis-dev/doculat-landing); a campaign link straight to the app carries utm_* itself. The app keeps
// the first one for 30 days in this browser and sends it with a sign-up and with a company registered.
// About the visit, not the visitor: no identifier.

export interface Attribution {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  referrer: string | null;
  landing: string | null;
  firstVisit: string | null;
}

const KEY = 'teamfolio.source';
const KEEP_MS = 30 * 24 * 3600_000;
const PARAMS: Record<keyof Attribution, string> = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  referrer: 'tf_ref',
  landing: 'tf_landing',
  firstVisit: 'tf_first',
};

const clip = (value: string | null, length: number) => (value ? value.trim().slice(0, length) || null : null);

/** What an address carries, or null when it carries nothing of it. Pure, so it is tested alone. */
export function attributionFrom(search: string, today: string): Attribution | null {
  const query = new URLSearchParams(search);
  if (!Object.values(PARAMS).some((param) => query.has(param))) return null;
  return {
    utmSource: clip(query.get(PARAMS.utmSource), 100),
    utmMedium: clip(query.get(PARAMS.utmMedium), 100),
    utmCampaign: clip(query.get(PARAMS.utmCampaign), 100),
    referrer: clip(query.get(PARAMS.referrer), 253),
    landing: clip(query.get(PARAMS.landing), 200),
    firstVisit: clip(query.get(PARAMS.firstVisit), 10) ?? today,
  };
}

/** The address without those parameters, so they are not bookmarked or shared on. */
export function withoutAttribution(search: string): string {
  const query = new URLSearchParams(search);
  for (const param of Object.values(PARAMS)) query.delete(param);
  const rest = query.toString();
  return rest ? `?${rest}` : '';
}

function read<T>(key: string): T | null {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null') as T | null;
  } catch {
    return null;
  }
}
function write(key: string, value: unknown): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage refused (a private window): nothing is remembered.
  }
}

/** The attribution kept in this browser, while it is under 30 days old. */
export function storedAttribution(): Attribution | null {
  const kept = read<{ attribution: Attribution; at: number }>(KEY);
  return kept && Date.now() - kept.at < KEEP_MS ? kept.attribution : null;
}

/**
 * On opening the app: keeps what the address carries, unless an earlier visit is kept already (the
 * first one counts), and takes the parameters out of the address.
 */
export function captureAttribution(): void {
  const found = attributionFrom(location.search, new Date().toISOString().slice(0, 10));
  if (!found) return;
  if (!storedAttribution()) write(KEY, { attribution: found, at: Date.now() });
  history.replaceState(history.state, '', `${location.pathname}${withoutAttribution(location.search)}${location.hash}`);
}

